import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { MessageSquare, RefreshCw, Smartphone, QrCode } from 'lucide-react';
import { isApiError } from '../../lib/api';
import {
  checkWhatsAppCloud,
  connectWhatsApp,
  disconnectWhatsApp,
  getWhatsAppCloud,
  getWhatsAppStatus,
  saveWhatsAppCloud,
  testWhatsAppCloud,
  type WhatsAppCloudConfig,
  type WhatsAppCloudPatch,
  type WhatsAppCloudResult,
  type WhatsAppInfo,
} from '../../lib/whatsapp-api';
import { Button, Badge } from '../../components/ui';
import styles from './admin.module.css';
import { confirmDialog } from '../../stores/confirm';
import { SecretInput } from './SecretInput';

// System-wide WhatsApp notification transport (super-admin). One company
// number sends every site's WhatsApp visitor-notification webhooks, either
// through the official Cloud API (recommended) or a legacy Baileys linked
// number — the unofficial WhatsApp Web protocol, which carries a ban risk.
export function AdminWhatsApp() {
  return (
    <div>
      <div className={styles.headerRow}>
        <div className={styles.headerTitles}>
          <div className={styles.kicker}>
            <MessageSquare size={13} />
            <span>Platform Engine</span>
          </div>
          <h1 className={styles.title}>WhatsApp Gateway</h1>
          <p className={styles.subtitle}>
            One company WhatsApp number sends agent notifications across every site. Create a{' '}
            <strong>WhatsApp message</strong> webhook under a site’s settings (e.g. on “Visitor
            identified”) and it’s delivered to every site agent who has a phone number on file.
          </p>
        </div>
      </div>

      <CloudApiPanel />
      <BaileysPanel />
    </div>
  );
}

type Mode = 'auto' | 'cloud' | 'baileys';

const modeOf = (c: WhatsAppCloudConfig): Mode => c.provider ?? 'auto';

const SAVE_ERRORS: Record<string, string> = {
  'cloud-incomplete': 'Fill in every Cloud API field before switching to it.',
  'encryption-unavailable': 'CREDENTIALS_ENC_KEY is not set on the server, so the token can’t be stored.',
  'invalid-body': 'Check the fields: the phone number ID is digits only, the template name is lowercase with underscores, and the language looks like en or en_US.',
};

function CloudApiPanel() {
  const tokenId = useId();
  const [cfg, setCfg] = useState<WhatsAppCloudConfig | null>(null);
  const [mode, setMode] = useState<Mode>('auto');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [token, setToken] = useState('');
  const [clearToken, setClearToken] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateLanguage, setTemplateLanguage] = useState('en');
  const [testPhone, setTestPhone] = useState('');
  const [busy, setBusy] = useState<null | 'save' | 'check' | 'test'>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ kind: 'check' | 'test'; r: WhatsAppCloudResult } | null>(null);

  const apply = useCallback((c: WhatsAppCloudConfig) => {
    setCfg(c);
    setMode(modeOf(c));
    setPhoneNumberId(c.phoneNumberId ?? '');
    setTemplateName(c.templateName ?? '');
    setTemplateLanguage(c.templateLanguage ?? 'en');
    setToken('');
    setClearToken(false);
  }, []);

  useEffect(() => {
    getWhatsAppCloud()
      .then(apply)
      .catch(() => setError('Could not load the Cloud API settings.'));
  }, [apply]);

  async function handleSave() {
    setBusy('save');
    setError(null);
    setResult(null);
    const patch: WhatsAppCloudPatch = {
      provider: mode === 'auto' ? null : mode,
      phoneNumberId: phoneNumberId.trim() || null,
      templateName: templateName.trim() || null,
      templateLanguage: templateLanguage.trim() || null,
    };
    if (clearToken) patch.accessToken = null;
    else if (token.trim()) patch.accessToken = token.trim();
    try {
      apply(await saveWhatsAppCloud(patch));
    } catch (err) {
      const code = isApiError(err) ? (err.body as { error?: string } | null)?.error : undefined;
      setError((code && SAVE_ERRORS[code]) ?? 'Could not save the Cloud API settings.');
    } finally {
      setBusy(null);
    }
  }

  async function run(kind: 'check' | 'test') {
    setBusy(kind);
    setError(null);
    setResult(null);
    try {
      setResult({ kind, r: kind === 'check' ? await checkWhatsAppCloud() : await testWhatsAppCloud(testPhone) });
    } catch (err) {
      const invalidPhone = isApiError(err) && (err.body as { error?: string } | null)?.error === 'invalid-phone';
      setError(invalidPhone ? 'Enter the test number in international format, e.g. +91 98765 43210.' : 'The request failed.');
    } finally {
      setBusy(null);
    }
  }

  const sending = cfg?.effectiveProvider === 'cloud' ? 'the Cloud API' : 'the Baileys linked number';
  const unsaved =
    !!cfg &&
    (mode !== modeOf(cfg) ||
      phoneNumberId !== (cfg.phoneNumberId ?? '') ||
      templateName !== (cfg.templateName ?? '') ||
      templateLanguage !== (cfg.templateLanguage ?? 'en') ||
      token !== '' ||
      clearToken);

  return (
    <div className={styles.tableWrap} style={{ padding: 'var(--inic-space-5)', marginBottom: 'var(--inic-space-5)' }}>
      <h3 className={styles.label} style={{ margin: '0 0 var(--inic-space-2)', fontSize: 'var(--inic-size-sm)' }}>
        WhatsApp Cloud API (official)
      </h3>
      <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)', lineHeight: 1.6, maxWidth: 620, marginTop: 0 }}>
        Meta’s hosted API. Notifications are business-initiated, so WhatsApp only delivers them as an{' '}
        <strong>approved message template</strong>. In WhatsApp Manager, create a <em>Utility</em> template
        whose body has exactly one variable, for example <span className={styles.mono}>Openline alert: {'{{1}}'}</span>;
        the webhook’s rendered text fills it in (line breaks become “ · ”). Use a System User token with the{' '}
        <span className={styles.mono}>whatsapp_business_messaging</span> permission.
      </p>

      {cfg && (
        <p className={styles.notice} style={{ marginBottom: 'var(--inic-space-4)' }}>
          Notifications are currently sent through <strong>{sending}</strong>.
        </p>
      )}
      {error && <p className={styles.error} role="alert">{error}</p>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--inic-space-4)' }}>
        <label className={styles.field}>
          <span className={styles.label}>Send notifications through</span>
          <select className={styles.select} value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
            <option value="auto">Automatic (Cloud API once set up)</option>
            <option value="cloud">Cloud API</option>
            <option value="baileys">Baileys linked number (legacy)</option>
          </select>
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Phone number ID</span>
          <input
            className={styles.input}
            inputMode="numeric"
            value={phoneNumberId}
            onChange={(e) => setPhoneNumberId(e.target.value)}
            placeholder="e.g. 106540352242922"
          />
          <span className={styles.hint}>WhatsApp Manager → Phone numbers (not the phone number itself).</span>
        </label>

        {/* A div, not a <label>: a label wrapping the Remove button would make
            that button its control, so clicking "Access token" pressed Remove
            and the input had no accessible name. */}
        <div className={styles.field}>
          <span className={styles.fieldHead}>
            <label htmlFor={tokenId} className={styles.label}>
              Access token{' '}
              <em style={{ fontStyle: 'normal' }}>
                {cfg?.tokenSet && !clearToken ? `— saved (••••${cfg.tokenLast4 ?? '????'})` : '— not set'}
              </em>
            </label>
            {cfg?.tokenSet && !clearToken && (
              <button
                type="button"
                className={styles.buttonDanger}
                title="Remove the saved access token"
                onClick={() => {
                  setClearToken(true);
                  setToken('');
                }}
              >
                Remove
              </button>
            )}
          </span>
          <SecretInput
            id={tokenId}
            value={token}
            onChange={(e) => {
              setToken(e.target.value);
              setClearToken(false);
            }}
            placeholder={cfg?.tokenSet && !clearToken ? 'Leave blank to keep current token' : 'System User access token'}
          />
          <span className={styles.hint}>
            {clearToken ? 'The saved token will be removed when you save.' : 'Stored encrypted, and never shown again once saved.'}
          </span>
        </div>

        <label className={styles.field}>
          <span className={styles.label}>Template name</span>
          <input
            className={styles.input}
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="openline_alert"
          />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Template language</span>
          <input
            className={styles.input}
            value={templateLanguage}
            onChange={(e) => setTemplateLanguage(e.target.value)}
            placeholder="en"
          />
          <span className={styles.hint}>Exactly as approved, e.g. en or en_US.</span>
        </label>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--inic-space-3)', marginTop: 'var(--inic-space-4)' }}>
        <button type="button" className={styles.button} onClick={() => void handleSave()} disabled={busy !== null || !unsaved}>
          {busy === 'save' ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className={styles.buttonGhost}
          onClick={() => void run('check')}
          disabled={busy !== null || unsaved || !cfg?.tokenSet || !cfg.phoneNumberId}
          title={unsaved ? 'Save first' : undefined}
        >
          {busy === 'check' ? 'Checking…' : 'Check connection'}
        </button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 'var(--inic-space-3)', marginTop: 'var(--inic-space-4)' }}>
        <label className={styles.field} style={{ flex: '1 1 220px', maxWidth: 320 }}>
          <span className={styles.label}>Send a test to</span>
          <input
            className={styles.input}
            type="tel"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            placeholder="+91 98765 43210"
          />
        </label>
        <button
          type="button"
          className={styles.buttonGhost}
          onClick={() => void run('test')}
          disabled={busy !== null || unsaved || !cfg?.configured || !testPhone.trim()}
          title={unsaved ? 'Save first' : undefined}
        >
          {busy === 'test' ? 'Sending…' : 'Send test'}
        </button>
      </div>

      {result && (
        <p className={result.r.ok ? styles.notice : styles.error} style={{ marginTop: 'var(--inic-space-4)' }} role="status">
          {!result.r.ok
            ? result.r.detail
            : result.kind === 'check'
              ? `Connected: ${result.r.verifiedName || 'business number'} · ${result.r.displayPhoneNumber ?? ''}${
                  result.r.qualityRating ? ` · quality ${result.r.qualityRating.toLowerCase()}` : ''
                }`
              : 'Test sent. It should arrive within a few seconds.'}
        </p>
      )}
    </div>
  );
}

function BaileysPanel() {
  const [info, setInfo] = useState<WhatsAppInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      setInfo(await getWhatsAppStatus());
    } catch {
      setError('Could not load WhatsApp status.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll while the link is in progress (connecting / awaiting QR scan) so the
  // QR and the connected transition appear without a manual refresh.
  useEffect(() => {
    const live = info?.status === 'connecting' || info?.status === 'qr_pending';
    if (live && !timer.current) {
      timer.current = setInterval(() => void load(), 2000);
    } else if (!live && timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
    return () => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    };
  }, [info?.status, load]);

  async function handleConnect() {
    setBusy(true);
    setError(null);
    try {
      setInfo(await connectWhatsApp());
    } catch {
      setError('Could not start the WhatsApp connection.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDisconnect() {
    if (!(await confirmDialog({ title: 'Unlink this WhatsApp number?', message: 'Notifications stop until you link again.', confirmLabel: 'Unlink' }))) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await disconnectWhatsApp();
      await load();
    } catch {
      setError('Could not disconnect.');
    } finally {
      setBusy(false);
    }
  }

  const status = info?.status ?? 'disconnected';

  return (
    <>
      <div className={styles.headerRow} style={{ marginTop: 'var(--inic-space-5)' }}>
        <div className={styles.headerTitles}>
          <h3 className={styles.label} style={{ margin: 0, fontSize: 'var(--inic-size-sm)' }}>
            Linked number via Baileys (legacy)
          </h3>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Badge
            variant="outline"
            className={`text-xs px-2.5 py-1 ${
              status === 'connected'
                ? 'text-emerald-500 border-emerald-500/20 bg-emerald-500/10 font-normal'
                : status === 'qr_pending' || status === 'connecting'
                ? 'text-amber-500 border-amber-500/20 bg-amber-500/10 font-normal'
                : 'text-muted-foreground font-normal'
            }`}
          >
            {status === 'connected' && 'Connected'}
            {status === 'connecting' && 'Connecting…'}
            {status === 'qr_pending' && 'Awaiting QR Scan'}
            {status === 'disconnected' && 'Not Connected'}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void load()}
            className="gap-2 h-9 px-3 text-xs"
          >
            <RefreshCw size={13} />
            <span>Refresh</span>
          </Button>
        </div>
      </div>

      {error && <p className={styles.error} style={{ marginBottom: 16 }} role="alert">{error}</p>}

      <section className={styles.section} style={{ padding: 24, borderRadius: 12 }}>
        <p style={{ color: 'var(--inic-ink-muted)', fontSize: 13, lineHeight: 1.6, maxWidth: 640, margin: '0 0 20px' }}>
          Baileys uses the unofficial WhatsApp Web protocol: the linked number can be banned without
          notice. Kept as a fallback while you set up the Cloud API; it will be removed in a later release.
        </p>

        {status === 'connected' && (
          <div style={{ background: 'var(--inic-card-bg)', border: '1px solid var(--inic-card-border)', borderRadius: 10, padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <Smartphone size={18} className="text-emerald-500" />
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--inic-ink)' }}>
                {info?.phoneName || 'Company WhatsApp'}
              </span>
              {info?.phoneNumber && (
                <span className={styles.mono} style={{ fontSize: 13, color: 'var(--inic-ink-muted)' }}>
                  +{info.phoneNumber}
                </span>
              )}
            </div>
            {info?.connectedAt && (
              <p style={{ margin: '0 0 16px', color: 'var(--inic-ink-subtle)', fontSize: 12 }}>
                Active since {new Date(info.connectedAt).toLocaleString()}
              </p>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleDisconnect()}
              disabled={busy}
              className="text-destructive hover:bg-destructive/10 h-8 px-3 text-xs"
            >
              {busy ? 'Working…' : 'Disconnect Number'}
            </Button>
          </div>
        )}

        {status === 'qr_pending' && info?.qr && (
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center', background: 'var(--inic-card-bg)', border: '1px solid var(--inic-card-border)', borderRadius: 10, padding: 20 }}>
            <div style={{ background: '#ffffff', padding: 10, borderRadius: 8, border: '1px solid var(--inic-rule)' }}>
              <img
                src={info.qr}
                alt="WhatsApp linking QR code"
                width={200}
                height={200}
                style={{ display: 'block' }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <h4 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 600, color: 'var(--inic-ink)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <QrCode size={16} />
                <span>Scan with WhatsApp on your phone</span>
              </h4>
              <ol style={{ color: 'var(--inic-ink-muted)', fontSize: 12, lineHeight: 1.8, margin: 0, paddingLeft: 18 }}>
                <li>Open WhatsApp on the company phone.</li>
                <li>Tap <strong>Settings → Linked devices → Link a device</strong>.</li>
                <li>Point camera at this code. It refreshes automatically until linked.</li>
              </ol>
            </div>
          </div>
        )}

        {status === 'connecting' && (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--inic-ink-muted)', fontSize: 13 }}>
            <RefreshCw size={20} className="animate-spin mx-auto mb-2 text-primary" />
            <p>Establishing the connection — a QR code will appear shortly…</p>
          </div>
        )}

        {status === 'disconnected' && (
          <div style={{ marginTop: 8 }}>
            <Button
              size="sm"
              onClick={() => void handleConnect()}
              disabled={busy}
              className="h-10 px-5 gap-2 text-xs font-semibold"
            >
              <Smartphone size={15} />
              <span>{busy ? 'Starting…' : 'Connect WhatsApp Number →'}</span>
            </Button>
          </div>
        )}
      </section>
    </>
  );
}
